// Weighbridge Kiosk Mode — typed API helpers.
//
// The kiosk authenticates in the SAME tenant token slots as the regular web
// app (accessToken / refreshToken in storage) so every existing /wb/* hook
// and the apiClient refresh interceptor work unchanged. The difference is
// purely the login surface: a 4-digit PIN (or password fallback) instead of
// the username+password form.

import { apiClient } from './client';
import { endpoints } from './endpoints';
import type { StaffRole } from '../types/api';

export interface KioskStaffSummary {
  readonly enc_id: string;
  readonly display_name: string;
  readonly has_pin: boolean;
}

export interface KioskLoginResult {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly staff: {
    readonly enc_id: string;
    readonly name: string;
    readonly username: string;
    readonly role: StaffRole;
  };
}

/** Sentinel thrown by `kioskLogin` when the staff member has no PIN set. */
export class NoPinSetError extends Error {
  constructor() {
    super('NO_PIN_SET');
    this.name = 'NoPinSetError';
  }
}

/** List active staff for the weighbridge this kiosk is installed on. */
export async function fetchKioskStaff(
  weighbridgeEncId: string,
): Promise<KioskStaffSummary[]> {
  const res = await apiClient.get<KioskStaffSummary[]>(
    `${endpoints.auth.kioskStaff}?weighbridge_enc_id=${encodeURIComponent(weighbridgeEncId)}`,
  );
  return res.data;
}

/**
 * PIN login. Throws `NoPinSetError` when the server reports the staff
 * member has no PIN configured (the caller should fall back to the password
 * form); rethrows other errors (wrong PIN → 401, lockout → 429).
 */
export async function kioskLogin(
  staffEncId: string,
  pin: string,
): Promise<KioskLoginResult> {
  try {
    const res = await apiClient.post<KioskLoginResult>(endpoints.auth.kioskLogin, {
      staff_enc_id: staffEncId,
      pin,
    });
    return res.data;
  } catch (err) {
    // The NO_PIN_SET response is a bare { error: 'NO_PIN_SET' } with status
    // 400 (not the standard envelope), so detect it on the axios error.
    const data = (err as { response?: { data?: { error?: string } } }).response?.data;
    if (data?.error === 'NO_PIN_SET') {
      throw new NoPinSetError();
    }
    throw err;
  }
}

/** Whether the current staff member already has a kiosk PIN configured. */
export async function getKioskPinStatus(): Promise<boolean> {
  const res = await apiClient.get<{ has_pin: boolean }>(endpoints.staff.pinStatus);
  return res.data.has_pin;
}

/**
 * Set (or replace) the current staff member's 4-digit kiosk PIN. When a PIN
 * is already set, `currentPin` is required (the server verifies it; a wrong
 * value → 401).
 */
export async function setKioskPin(pin: string, currentPin?: string): Promise<void> {
  await apiClient.post(endpoints.staff.setPin, {
    pin,
    ...(currentPin ? { current_pin: currentPin } : {}),
  });
}

/** Remove the current staff member's kiosk PIN. Requires the current PIN. */
export async function removeKioskPin(currentPin: string): Promise<void> {
  await apiClient.delete(endpoints.staff.removePin, { data: { current_pin: currentPin } });
}
